import { formatUnits } from 'ethers/lib/utils';
import { useEffect, useState } from 'react';

import { Vault } from '@/features/vaults/types';
import { useCoreVaultData } from '@/features/vaults/hooks/useCoreVaultData';
import { useVaultsData } from '@/features/vaults/hooks/useVaultsData';
import { extractAprOrApy } from '@/pages/VaultPage/RewardsDisplay/utils/extractAprOrApy';
import { supportedTokens } from '@/features/vaults/useTokens';
import { rootstockTyrRbtcVaultId } from '@/features/vaults/constants/vaults/rootstock/rootstock-tyr-rbtc-vault';
import { lidoEarnEthVaultId } from '@/features/vaults/constants/vaults/lido/lido-earn-eth';
import { lidoEarnUsdVaultId } from '@/features/vaults/constants/vaults/lido/lido-earn-usd';
import { UseVaultTvlAndApyResult } from '@/features/vaults/types/UseVaultTvlAndApyResult';
import { jsonStringify } from '@/shared/utils/format/jsonStringify';
import { NETWORK_CHAIN } from '@/core/config';

const defaultApy: UseVaultTvlAndApyResult['apy'] = {
  value: 0,
  lastUpdate: '',
  type: 'APY',
};

function useVaultTvlAndApyCore(vault: Vault): UseVaultTvlAndApyResult {
  const vaultsData = useVaultsData();
  const coreVaultData = useCoreVaultData(vault, { enabled: true, staleTime: 3 * 60 * 1000 });

  const vaultData = vaultsData.data?.find(
    e => e.chain_id === NETWORK_CHAIN[vault.network] && e.address === vault.address,
  );

  const apy = coreVaultData?.data?.apy ? +coreVaultData.data.apy : 0;
  const apyLastUpdate = coreVaultData?.data?.apyLastUpdate || '';

  const symbol =
    supportedTokens[vault.network]?.[
      vault.baseToken || vaultData?.underlying_tokens[0]?.address || ''
    ]?.symbol || '';

  return {
    tvl: {
      value: vaultData?.tvl_base_token_aggregate
        ? +formatUnits(
            vaultData.tvl_base_token_aggregate,
            vaultData.base_token?.decimals || vaultData.decimals,
          )
        : 0,
      symbol: symbol || vaultData?.symbol || vault.lpTokenSymbol,
      usd: vaultData?.tvl_usd_aggregate ? +vaultData?.tvl_usd_aggregate : 0,
    },
    apy: {
      value: apy,
      type: 'APY',
      lastUpdate: apyLastUpdate,
    },
    loading: vaultsData.isLoading || coreVaultData.isLoading,
    success: vaultsData.isSuccess && coreVaultData.isSuccess,
  };
}

function useVaultTvlAndApyRestaking(vault: Vault): UseVaultTvlAndApyResult {
  const { data: vaultsData, isLoading } = useVaultsData();
  const [apy, setApy] = useState<UseVaultTvlAndApyResult['apy']>(defaultApy);

  const vaultData = vaultsData?.find(e =>
    vault.id === rootstockTyrRbtcVaultId
      ? e.id === rootstockTyrRbtcVaultId
      : e.address === vault.address,
  );

  const apyOrAprBase = vaultData
    ? extractAprOrApy(vault.displayOptions?.aprOrApy || 'APY', vaultData)
    : null;

  useEffect(() => {
    if (vault.apyLoader) {
      vault.apyLoader(vault.address, vault.predictedApy).then(result => {
        if (result) {
          setApy({
            value: result.value,
            type: apyOrAprBase?.type || 'APY',
            lastUpdate: '',
          });
        } else {
          setApy(defaultApy);
        }
      });
      return;
    } else if (vault.predictedApy) {
      setApy({
        ...vault.predictedApy,
        lastUpdate: '',
      });
      return;
    } else if (vaultData) {
      const value = extractAprOrApy('APY', vaultData);

      if (value) {
        setApy({
          ...value,
          lastUpdate: '',
        });
        return;
      }
    }
    setApy(defaultApy);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [jsonStringify({ vault: vault.address, vaultData, apyOrAprBase })]);

  const symbol =
    supportedTokens[vault.network]?.[
      vault.baseToken || vaultData?.underlying_tokens[0]?.address || ''
    ]?.symbol || '';

  return {
    tvl: {
      value: vaultData?.tvl_base_token_aggregate
        ? +formatUnits(
            vaultData.tvl_base_token_aggregate,
            vaultData.base_token?.decimals || vaultData.decimals,
          )
        : 0,
      symbol: symbol || vaultData?.symbol || vault.lpTokenSymbol,
      usd: vaultData?.tvl_usd_aggregate ? +vaultData?.tvl_usd_aggregate : 0,
    },
    apy,
    loading: isLoading,
    success: !!vaultData,
  };
}

export function useVaultTvlAndApy(vault: Vault): UseVaultTvlAndApyResult {
  if ([lidoEarnEthVaultId, lidoEarnUsdVaultId].includes(vault.id)) {
    return useVaultTvlAndApyCore(vault);
  }

  return useVaultTvlAndApyRestaking(vault);
}
